import { ChevronDown, Clock, Home, MapPin, Phone } from "lucide-react"
import { useMemo, useState, type ReactNode } from "react"
import { useTranslation } from "react-i18next"
import moment from "moment"
import "moment/locale/id"
import { Project } from "../../../types/project.type"
import { resolveOptionalColor, resolveThemeColor } from "../../../utils/buttonColor"

function InformationSection({ project }: { project: Project }) {
  const { t, i18n } = useTranslation('home')
  const themeColor = resolveThemeColor(project.themeColor)
  const buttonColor = resolveThemeColor(project.buttonColor)
  const serviceColor = resolveOptionalColor(project.serviceColor)

  const waHref = useMemo(() => {
    const raw = project.contactNo?.replace(/\D/g, "") ?? ""
    return raw ? `https://wa.me/${raw}` : undefined
  }, [project.contactNo])

  // Re-render day names when language changes (moment.locale is synced in i18n).
  const localeKey = i18n.language

  return (
    <section
      className="mx-5 mt-8 mb-8 overflow-hidden rounded-2xl border border-gray-200/90 bg-white text-gray-900 shadow-xl shadow-gray-900/5 sm:mt-10"
      id="information"
    >
      <header
        className="border-b border-gray-100 px-5 py-6 text-center sm:px-8 sm:py-8"
        style={{
          background: `linear-gradient(135deg, ${themeColor}0d 0%, transparent 55%), linear-gradient(to bottom, #fafafa, #ffffff)`,
        }}
      >
        <h2 className="text-2xl font-semibold tracking-tight text-gray-900">{t('contactHoursTitle')}</h2>
        <p className="mx-auto mt-1.5 max-w-lg text-sm leading-relaxed text-gray-600">
          {t('contactHoursSubtitle')}
        </p>
      </header>

      <div className="grid gap-4 p-5 sm:gap-5 sm:p-8">
        <div className="grid gap-4 sm:grid-cols-2">
          <InfoCard
            themeColor={themeColor}
            serviceColor={serviceColor}
            icon={<MapPin className="h-6 w-6" strokeWidth={2} style={{ color: themeColor }} aria-hidden />}
            title={t('address')}
            content={
              project.addressLink ? (
                <a
                  href={project.addressLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium leading-relaxed text-gray-700 underline decoration-gray-300 underline-offset-2 transition hover:decoration-gray-500"
                >
                  {project.address}
                </a>
              ) : (
                <p className="text-sm font-medium leading-relaxed text-gray-800">{project.address}</p>
              )
            }
          />

          <InfoCard
            themeColor={themeColor}
            serviceColor={serviceColor}
            icon={<Phone className="h-6 w-6" strokeWidth={2} style={{ color: themeColor }} aria-hidden />}
            title={t('contact')}
            content={
              waHref ? (
                <a
                  href={waHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex text-sm font-semibold transition hover:opacity-90"
                  style={{ color: buttonColor }}
                >
                  {project.contactNo}
                </a>
              ) : (
                <p className="text-sm font-semibold text-gray-800">{project.contactNo}</p>
              )
            }
            subtitle={t('whatsappOrPhone')}
          />
        </div>

        {project.operation?.onsite?.hours?.length ? (
          <CollapsibleHoursBlock
            key={`onsite-${localeKey}`}
            panelId="hours-onsite"
            icon={<Home className="h-6 w-6" strokeWidth={2} style={{ color: themeColor }} aria-hidden />}
            title={t('hoursOnsite')}
            hours={project.operation.onsite.hours}
            themeColor={themeColor}
            serviceColor={serviceColor}
          />
        ) : null}

        {project.operation?.offsite?.hours?.length ? (
          <CollapsibleHoursBlock
            key={`offsite-${localeKey}`}
            panelId="hours-offsite"
            icon={<Clock className="h-6 w-6" strokeWidth={2} style={{ color: themeColor }} aria-hidden />}
            title={t('hoursOffsite')}
            hours={project.operation.offsite.hours}
            themeColor={themeColor}
            serviceColor={serviceColor}
          />
        ) : null}
      </div>
    </section>
  )
}

function InfoCard({
  icon,
  title,
  subtitle,
  content,
  themeColor,
  serviceColor,
}: {
  icon: ReactNode
  title: string
  subtitle?: string
  content: ReactNode
  themeColor: string
  serviceColor?: string
}) {
  return (
    <div
      className="rounded-xl border border-gray-200/90 p-4 shadow-sm sm:p-5"
      style={{
        borderLeftWidth: 4,
        borderLeftColor: themeColor,
        ...(serviceColor
          ? {
              background: `linear-gradient(145deg, ${serviceColor}0a 0%, #ffffff 42%)`,
            }
          : { backgroundColor: '#ffffff' }),
        borderTopColor: '#e5e7eb',
        borderRightColor: '#e5e7eb',
        borderBottomColor: '#e5e7eb',
      }}
    >
      <div className="flex gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-gray-50 ring-1 ring-gray-100">
          {icon}
        </div>
        <div className="min-w-0 flex-1 text-left">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{title}</p>
          {subtitle ? <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p> : null}
          <div className="mt-2">{content}</div>
        </div>
      </div>
    </div>
  )
}

function CollapsibleHoursBlock({
  icon,
  title,
  hours,
  themeColor,
  serviceColor,
  defaultOpen = false,
  panelId,
}: {
  icon: ReactNode
  title: string
  hours: {
    isOpen: boolean
    time: { openTime: string; closeTime: string }[]
  }[]
  themeColor: string
  serviceColor?: string
  defaultOpen?: boolean
  panelId: string
}) {
  const { t } = useTranslation('home')
  const [open, setOpen] = useState(defaultOpen)
  const headingId = `${panelId}-heading`

  return (
    <div
      className="overflow-hidden rounded-xl border border-gray-200/90 shadow-sm"
      style={{
        borderLeftWidth: 4,
        borderLeftColor: themeColor,
        ...(serviceColor
          ? {
              background: `linear-gradient(145deg, ${serviceColor}0a 0%, #ffffff 42%)`,
            }
          : { backgroundColor: '#ffffff' }),
        borderTopColor: '#e5e7eb',
        borderRightColor: '#e5e7eb',
        borderBottomColor: '#e5e7eb',
      }}
    >
      <button
        type="button"
        id={headingId}
        className="flex w-full items-center gap-3 px-4 py-4 text-left transition hover:bg-gray-50/80 sm:px-5 sm:py-5"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
      >
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-gray-50 ring-1 ring-gray-100">
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{title}</p>
          <p className="mt-0.5 text-sm text-gray-600">{t('weeklySchedule')}</p>
        </div>
        <div style={{ transform: open ? 'rotate(180deg)' : undefined }}>
          <ChevronDown
            className="h-5 w-5 shrink-0"
            style={{ color: themeColor }}
            aria-hidden
          />
        </div>
      </button>

      {open && (
      <div
        id={panelId}
        role="region"
        aria-labelledby={headingId}
        className="overflow-hidden border-t border-gray-100"
      >
        <ul className="list-none divide-y divide-gray-100 px-3 pb-4 pt-2 text-sm sm:px-5 sm:pt-3">
          {hours.map((hour, index) => (
            <li key={index} className="py-3.5 first:pt-2">
              <div className="grid grid-cols-[minmax(6.25rem,auto)_minmax(0,1fr)] items-start gap-x-3 sm:grid-cols-[9.5rem_1fr] sm:gap-x-6">
                <span className="font-semibold capitalize leading-snug text-gray-900">
                  {moment().day(index).format("dddd")}
                </span>
                <div
                  className={`min-w-0 space-y-1 leading-snug sm:text-right ${
                    hour.isOpen ? "text-emerald-800" : "text-gray-500"
                  }`}
                >
                  {hour.isOpen ? (
                    hour.time?.length ? (
                      hour.time.map((time, timeIndex) => (
                        <div
                          key={timeIndex}
                          className="tabular-nums tracking-tight sm:ml-auto sm:max-w-none sm:whitespace-nowrap"
                        >
                          {time.openTime}
                          <span className="mx-1 text-gray-400" aria-hidden>
                            –
                          </span>
                          {time.closeTime}
                        </div>
                      ))
                    ) : (
                      <span>{t('open')}</span>
                    )
                  ) : (
                    <span>{t('closed')}</span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
      )}
    </div>
  )
}

export default InformationSection
